import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Ban,
  Clock,
  Edit,
  Trash2,
  User,
  Briefcase,
  Calendar,
  TrendingUp,
  Send,
} from 'lucide-react'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import Modal from '@/components/common/Modal'
import { toast } from '@/stores/toastStore'
import { recruiterJobOfferService } from '@/services/jobOfferService'
import { useQueryClient } from '@tanstack/react-query'
import { jobProspectsKeys } from '@/hooks/useJobProspects'
import { JobOffer, JobOfferStatus } from '@/types'
import { formatEmploymentInfo } from '@/utils/jobOfferUtils'

export default function SentOffersTab() {
  const { t } = useTranslation('jobOffers')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(true)
  const [offers, setOffers] = useState<JobOffer[]>([])
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    viewRate: 0,
    responseRate: 0,
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })

  const [statusFilter, setStatusFilter] = useState<JobOfferStatus | ''>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [withdrawingOffer, setWithdrawingOffer] = useState<JobOffer | null>(null)

  useEffect(() => {
    fetchOffers()
  }, [statusFilter, pagination.page])

  const fetchOffers = async () => {
    try {
      setLoading(true)
      const response = await recruiterJobOfferService.listJobOffers({
        status: statusFilter || undefined,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      })

      setOffers(response.offers)
      setPagination(response.pagination)
      setStats({
        total: response.stats.total,
        pending: response.stats.pending,
        accepted: response.stats.accepted,
        rejected: response.stats.rejected,
        viewRate: response.stats.viewRate ?? 0,
        responseRate: response.stats.responseRate ?? 0,
      })
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('offers.errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleWithdrawOffer = async () => {
    if (!withdrawingOffer) return

    try {
      await recruiterJobOfferService.withdrawJobOffer(withdrawingOffer.id)
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      toast.success(t('offers.success.withdrawn'))
      setWithdrawingOffer(null)
      fetchOffers()
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('offers.errors.withdrawFailed'))
    }
  }

  const getStatusColor = (status: JobOfferStatus) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'VIEWED':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'WITHDRAWN':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-600 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status: JobOfferStatus) => {
    const icons = {
      PENDING: <Clock className="w-4 h-4" />,
      VIEWED: <Eye className="w-4 h-4" />,
      ACCEPTED: <CheckCircle className="w-4 h-4" />,
      REJECTED: <XCircle className="w-4 h-4" />,
      WITHDRAWN: <Ban className="w-4 h-4" />,
      EXPIRED: <Clock className="w-4 h-4" />,
    }
    return icons[status] || <Clock className="w-4 h-4" />
  }

  const canModifyOffer = (status: JobOfferStatus) => {
    return status === 'PENDING' || status === 'VIEWED'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const filteredOffers = offers.filter((offer) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      offer.position.toLowerCase().includes(query) ||
      offer.candidate?.firstName?.toLowerCase().includes(query) ||
      offer.candidate?.lastName?.toLowerCase().includes(query)
    )
  })

  const statusOptions = [
    { value: '', label: t('offers.filters.allStatuses') },
    { value: 'PENDING', label: t('offers.status.pending') },
    { value: 'VIEWED', label: t('offers.status.viewed') },
    { value: 'ACCEPTED', label: t('offers.status.accepted') },
    { value: 'REJECTED', label: t('offers.status.rejected') },
    { value: 'WITHDRAWN', label: t('offers.status.withdrawn') },
    { value: 'EXPIRED', label: t('offers.status.expired') },
  ]

  return (
    <div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t('offers.stats.total')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="bg-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t('offers.stats.pending')}</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>

        <Card className="bg-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t('offers.stats.accepted')}</p>
              <p className="text-3xl font-bold text-green-600">{stats.accepted}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="bg-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{t('offers.stats.viewRate')}</p>
              <p className="text-3xl font-bold text-blue-600">{stats.viewRate.toFixed(1)}%</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder={t('offers.filters.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="w-5 h-5" />}
            />
          </div>
          <div className="md:w-64">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as JobOfferStatus | '')}
              options={statusOptions}
            />
          </div>
        </div>
      </Card>

      {/* Offers List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('common:status.loading')}</p>
        </div>
      ) : filteredOffers.length === 0 ? (
        <Card className="bg-white text-center py-12">
          <Send className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('offers.sent.empty.title')}</h3>
          <p className="text-gray-600 mb-6">{t('offers.sent.empty.description')}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOffers.map((offer) => (
            <Card key={offer.id} className="bg-white hover:shadow-xl transition-shadow">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {offer.candidate?.profilePhotoUrl ? (
                        <img
                          src={offer.candidate.profilePhotoUrl}
                          alt={`${offer.candidate.firstName} ${offer.candidate.lastName}`}
                          className="w-12 h-12 rounded-full rounded-avatar object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded-full rounded-avatar flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-500" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {offer.candidate?.firstName} {offer.candidate?.lastName}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(offer.status)}`}
                        >
                          {getStatusIcon(offer.status)}
                          {t(`offers.status.${offer.status.toLowerCase()}`)}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          <span className="font-medium">{offer.position}</span>
                        </div>
                        {(offer.employmentType || offer.workMode) && (
                          <span>{formatEmploymentInfo(offer.employmentType, offer.workMode)}</span>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(offer.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/recruiter/job-offers/${offer.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    {t('offers.actions.view')}
                  </Button>
                  {canModifyOffer(offer.status) && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/recruiter/job-offers/${offer.id}/edit`)}
                        className="flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        {t('offers.actions.edit')}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setWithdrawingOffer(offer)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('offers.actions.withdraw')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
          >
            {t('common:button.previous')}
          </Button>
          <span className="flex items-center px-4 text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.totalPages}
          >
            {t('common:button.next')}
          </Button>
        </div>
      )}

      {/* Withdraw confirmation modal (kept - not data entry) */}
      {withdrawingOffer && (
        <Modal
          isOpen={true}
          onClose={() => setWithdrawingOffer(null)}
          title={t('offers.withdraw.title')}
        >
          <div className="p-6">
            <p className="text-gray-600 mb-6">
              {t('offers.withdraw.message', {
                candidate: `${withdrawingOffer.candidate?.firstName} ${withdrawingOffer.candidate?.lastName}`,
                position: withdrawingOffer.position,
              })}
            </p>
            <div className="flex gap-3">
              <Button variant="danger" onClick={handleWithdrawOffer} className="flex-1">
                {t('offers.withdraw.confirm')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setWithdrawingOffer(null)}
                className="flex-1"
              >
                {t('offers.withdraw.cancel')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
